import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfileIATAPage.css';
import './PaymentPorterPage.css';
import paymentporterService from '../services/paymentporterService';
import { useAccount } from '../contexts/AccountContext';
import { formatDate } from '../utils/datetime';

const PPIcon = () => (
    <div className="pi-avatar" style={{ background: '#eef2ff' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </div>
);

const PaymentPorterListPage = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, selectedHotel, canCreateProducts } = useAccount();
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await paymentporterService.list({
                hotel_id:   selectedHotel || undefined,
                account_id: !selectedHotel && selectedAccount ? selectedAccount : undefined,
                partner_id: !selectedHotel && !selectedAccount && selectedPartner ? selectedPartner : undefined,
            });
            setItems(res.data?.paymentPorters || []);
        } catch (err) {
            console.error('Failed to load payment porters', err);
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, selectedPartner, selectedHotel]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Delete this Payment Porter?')) return;
        try {
            await paymentporterService.remove(id);
            setItems(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error('Failed to delete payment porter', err);
        }
    };

    const filtered = items.filter(p =>
        !search ||
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.hotel_name || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Apps
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Payment Porter</h1>
                        <p className="pi-subtitle">Manage automated payment processing configurations</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pp-refresh-btn" onClick={load} disabled={loading}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                    {/* Creating a product is an admin act ('A' on the property, its chain or its
                        partner). Hidden rather than disabled: a greyed button with no way to earn it
                        reads as a fault. The lambda enforces the same rule. */}
                    {canCreateProducts && (
                        <button className="pi-btn-add" onClick={() => navigate('/apps/payment-porter/new')}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            New Payment Porter
                        </button>
                    )}
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search by name, description or hotel..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="pi-list-panel">
                {filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#d1d5db" strokeWidth="1.5"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No Payment Porters match your search' : 'No Payment Porters configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by creating your first Payment Porter.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Hotel</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr
                                    key={p.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/apps/payment-porter/${p.id}`)}
                                >
                                    <td>
                                        <div className="pi-profile-cell">
                                            <PPIcon />
                                            <span className="pi-id-cell">{p.name}</span>
                                        </div>
                                    </td>
                                    <td>{p.description || <span className="pi-no-value">—</span>}</td>
                                    <td>{p.hotel_name || <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={p.schedule_enabled ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {p.schedule_enabled ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="pi-location-cell">{formatDate(p.created_at)}</td>
                                    <td>
                                        <div className="pi-actions" onClick={e => e.stopPropagation()}>
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${p.id}`)}
                                                title="Open"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={(e) => handleDelete(e, p.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PaymentPorterListPage;
